import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
// Light refresh: on mount + a gentle 60s interval. No heavy polling.
const REFRESH_MS = 60000;

/**
 * SelfRescueInboxPill — persistent admin top-bar pill showing the number of
 * actionable (new + in_review) Self-Rescue help requests. Read-only, no
 * customer names/emails/descriptions. Hidden entirely when count is 0.
 * Links to the admin Self-Rescue inbox. Admin-gated by the caller.
 */
export default function SelfRescueInboxPill({ ja = false }) {
  const [open, setOpen] = useState(null);
  const [hasNew, setHasNew] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/admin/self-rescue/count`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setOpen(d.open_count ?? 0);
        setHasNew(Boolean(d.new_count));
      } catch { /* non-fatal — pill simply stays hidden */ }
    };
    load();
    timer.current = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; if (timer.current) clearInterval(timer.current); };
  }, []);

  // Hidden until loaded and only when there is something actionable.
  if (!open) return null;

  const label = ja ? 'セルフレスキュー' : 'Self-Rescue';
  return (
    <Link
      to="/admin/self-rescue-requests"
      data-testid="topbar-self-rescue-pill"
      title={ja ? '未対応のセルフレスキュー依頼' : 'Open Self-Rescue help requests'}
      className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
        hasNew
          ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
          : 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
      }`}
    >
      <LifeBuoy className="w-3.5 h-3.5" strokeWidth={2} />
      <span data-testid="topbar-self-rescue-count">{open}</span>
      <span className="hidden md:inline">{label}</span>
    </Link>
  );
}
